<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { GebaeudeklasseNutzungsart, GebaeudeklasseNutzungsartCollection } from '@/models/GebaeudeklasseNutzungsart';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'gebaeudeklasse_code' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new GebaeudeklasseNutzungsartCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: GebaeudeklasseNutzungsart) {
    router.push({ name: 'bg_geb_gebaeudeklasse_nutzungsart_edit', params: { id: r?.id } });
}

async function deleteRecord(r: GebaeudeklasseNutzungsart) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_geb_gebaeudeklasse_nutzungsart_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('zuordnung_nutzungsart')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as GebaeudeklasseNutzungsart)"
            @deleterecord="deleteRecord($event as GebaeudeklasseNutzungsart)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem" :sort-order="-1"></Column>
            <Column
                field="gebaeudeklasse_i18n_key"
                sortable
                sort-field="gebaeudeklasse_code"
                :header="$t('gebaeudeklasse')"
                :sort-order="1"
            >
                <template #body="slotProps">
                    <div>
                        {{
                            $t(slotProps.data.gebaeudeklasse_i18n_key) + ' (' + slotProps.data.gebaeudeklasse_code + ')'
                        }}
                    </div>
                </template>
            </Column>
            <Column field="nutzungsart" :header="$t('nutzungsart')">
                <template #body="slotProps">
                    <div>{{ slotProps.data.nutzungsart }}</div>
                </template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
